import { TextField } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import theme from 'App.theme';
import Dialog from 'components/Dialog';
import { useLanguage } from 'context/AppContext';
import {
  getNextAvailableTrayNumber,
  PREPOPULATE_TRAY_NUMBERS,
  UnavailableTrayNumbers,
  useUnavailableTrayNumbers,
} from './UnavailableTrayNumbers';

type Props = {
  selectedNumber?: number | string | null;
  editedValues?: Record<number, string>;
  orderRecordId?: string;
  prepopulate?: boolean;
  // when true, skips rendering and confirms with the prepopulated number as soon as it's ready
  autoConfirm?: boolean;
  onClose: VoidFunction;
  onConfirmClick: (trayNumber: number) => void;
};

export const TrayNumberDialog = ({
  selectedNumber,
  editedValues,
  orderRecordId,
  prepopulate = true,
  autoConfirm = false,
  onClose,
  onConfirmClick,
}: Props) => {
  const lang = useLanguage()[0].TrayNumberDialog;

  const [trayNumberLocal, setTrayNumberLocal] = useState<number | null>(
    selectedNumber == null ? null : +selectedNumber,
  );
  const lastPrepopulatedRef = useRef<number | null>(null);

  const { unavailableTrayNumbers, isLoading: unavailableNumbersLoading } = useUnavailableTrayNumbers({
    selectedNumber,
    editedValues,
    orderRecordId,
  });

  const isError = trayNumberLocal != null && unavailableTrayNumbers.includes(trayNumberLocal);

  const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === '') {
      setTrayNumberLocal(null);
      return;
    }
    setTrayNumberLocal(Number(event.target.value));
  }, []);

  // keeps trayNumberLocal prepopulated regardless of whether the dialog below ends up rendering
  // (needed for the autoConfirm path further down)
  useEffect(() => {
    if (!PREPOPULATE_TRAY_NUMBERS || !prepopulate) {
      return;
    }
    setTrayNumberLocal(prev => {
      // Leave a value the user or existing record already provided.
      if (prev !== null && prev !== lastPrepopulatedRef.current) {
        return prev;
      }
      const next = getNextAvailableTrayNumber(unavailableTrayNumbers);
      lastPrepopulatedRef.current = next;
      return next;
    });
  }, [unavailableTrayNumbers, prepopulate]);

  // waits for the surgery-wide unavailable numbers to finish loading, so a number isn't confirmed
  // before we actually know what's taken
  const autoConfirmedRef = useRef(false);
  useEffect(() => {
    if (!autoConfirm || unavailableNumbersLoading || trayNumberLocal === null || isError || autoConfirmedRef.current) {
      return;
    }
    autoConfirmedRef.current = true;
    onConfirmClick(trayNumberLocal);
  }, [autoConfirm, unavailableNumbersLoading, trayNumberLocal, isError, onConfirmClick]);

  if (autoConfirm) {
    return null;
  }

  return (
    <Dialog
      open
      title={lang.title}
      confirmLabel={lang.confirm}
      confirmDisabled={!trayNumberLocal || isError}
      // allow zero
      onConfirm={() => {
        if (trayNumberLocal !== null) onConfirmClick(trayNumberLocal);
      }}
      onClose={onClose}
    >
      <UnavailableTrayNumbers
        orderRecordId={orderRecordId}
        selectedNumber={selectedNumber}
        editedValues={editedValues}
        currentTrayNumber={trayNumberLocal}
        onUnavailableNumbersChange={() => {}}
      />
      <TextField
        label={lang.inputLabel}
        placeholder={lang.inputLabel}
        value={trayNumberLocal !== null ? trayNumberLocal : ''}
        required
        error={isError}
        fullWidth
        onChange={onChange}
        sx={{
          marginTop: theme.layout.spacing.small,
        }}
        type="number"
        InputProps={{ inputProps: { min: 1 } }}
      />
    </Dialog>
  );
};
